import React, { useState, useEffect } from "react"
import { Col, Row, Spinner, Table, Nav, NavItem, NavLink, TabContent, TabPane } from "reactstrap"
import api from "API/Api"
import { AvForm, AvField, AvRadioGroup, AvRadio } from "availity-reactstrap-validation"
import jsPDF from "jspdf"
import "jspdf-autotable"
import { usePermissions } from "API/PermissionContext"
import classnames from "classnames"

const CleaningReport = () => {
  const { canExport, MODULE } = usePermissions()
  const MID = MODULE.REPORTS
  const [activeTab, setActiveTab] = useState("1")
  const [loading, setLoading] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [results, setResults] = useState([])
  const [allCenters, setAllCenters] = useState([])
  const [centers, setCenters] = useState([])
  const [routes, setRoutes] = useState([])
  const [allPlants, setAllPlants] = useState([])
  const [plants, setPlants] = useState([])
  const [collectionCenters, setCollectionCenters] = useState([])
  const [companies, setCompanies] = useState([])

  const today = new Date().toISOString().split("T")[0]

  useEffect(() => { loadData() }, [])
  useEffect(() => { setSubmitted(false); setResults([]); setRoutes([]) }, [activeTab])

  const loadData = async () => {
    try {
      const [cResp, pResp, ccResp, coResp] = await Promise.all([
        api.get("centers"), api.get("plants"),
        api.get("collection-centers"), api.get("companies")
      ])
      const ccData = Array.isArray(cResp.data.data) ? cResp.data.data : cResp.data.data?.data || []
      setAllCenters(ccData)
      setCenters(ccData)
      const pData = Array.isArray(pResp.data.data) ? pResp.data.data : pResp.data.data?.data || []
      setAllPlants(pData)
      setPlants(pData)
      setCollectionCenters(Array.isArray(ccResp.data.data) ? ccResp.data.data : ccResp.data.data?.data || [])
      setCompanies(Array.isArray(coResp.data.data) ? coResp.data.data : coResp.data.data?.data || [])
    } catch (e) { console.error(e) }
  }

  const companyChange = async e => {
    const val = e.target.value
    setCenters(allCenters); setRoutes([]); setCollectionCenters([])
    if (val && val !== "") {
      try { const r = await api.get("plants-by-company/" + val); setPlants(Array.isArray(r.data.data) ? r.data.data : r.data.data?.data || []) } catch(e){}
    } else setPlants(allPlants)
  }

  const plantChange = async e => {
    const val = e.target.value
    setRoutes([]); setCollectionCenters([])
    if (val && val !== "") {
      try {
        const r = await api.get("chillings-by-plant/" + val)
        setCenters(Array.isArray(r.data.data) ? r.data.data : r.data.data?.data || [])
      } catch (e) { console.error(e) }
    } else {
      setCenters(allCenters)
    }
  }

  const ccChange = async e => {
    try {
      const val = e.target.value
      const u = !val || val === "0" ? "routes" : "route-cc/" + val
      const r = await api.get(u)
      setRoutes(Array.isArray(r.data.data) ? r.data.data : r.data.data?.data || [])
    } catch (e) { console.error(e) }
  }

  const routeChange = async e => {
    try {
      const val = e.target.value
      if (val && val !== "0") {
        const r = await api.get("collection-center-route/" + val)
        setCollectionCenters(Array.isArray(r.data.data) ? r.data.data : r.data.data?.data || [])
      } else {
        const r = await api.get("collection-centers")
        setCollectionCenters(Array.isArray(r.data.data) ? r.data.data : r.data.data?.data || [])
      }
    } catch (e) { console.error(e) }
  }

  const isVlcc = activeTab === "1"
  const showCompany = companies.length > 1

  const handleSubmit = async (e, v) => {
    setLoading(true)
    try {
      const endpoint = isVlcc ? "reports/vlcc-cleaning" : "reports/cc-cleaning"
      const resp = await api.post(endpoint, v)
      if (resp.data.success) { setResults(resp.data.data || []); setSubmitted(true) }
      else { setResults([]); setSubmitted(true) }
    } catch (err) { console.error(err); setResults([]); setSubmitted(true) }
    setLoading(false)
  }

  const getStatusBadge = status => {
    if (status === "Good Cleaning") return <span className="badge bg-success">{status}</span>
    if (status === "Poor Cleaning") return <span className="badge bg-danger">{status}</span>
    return <span className="badge bg-secondary">{status}</span>
  }

  const downloadPdf = () => {
    const label = isVlcc ? "VLCC Cleaning Report" : "CC Cleaning Report"
    const pdf = new jsPDF("l")
    pdf.setFontSize(14)
    pdf.text(label, 14, 15)
    pdf.autoTable({ html: "#cleaningTbl", startY: 22, styles: { fontSize: 7 } })
    pdf.save(label.replace(/ /g, "-") + ".pdf")
  }

  const downloadCsv = () => {
    if (!results.length) return
    const label = isVlcc ? "VLCC-Cleaning-Report" : "CC-Cleaning-Report"
    let csv = ""
    if (isVlcc) {
      csv = "S.No,Date,Shift,Center,Route,CC,Plant,Start Time,Start FAT,Start SNF,Start Water%,Start Status,End Time,End FAT,End SNF,End Water%,End Status\n"
      results.forEach((r, i) => {
        csv += `${i + 1},"${r.date}","${r.shift}","${r.centerName}","${r.routeName}","${r.ccName}","${r.plantName}","${r.startTime}","${r.startFat}","${r.startSnf}","${r.startWater}","${r.startStatus}","${r.endTime}","${r.endFat}","${r.endSnf}","${r.endWater}","${r.endStatus}"\n`
      })
    } else {
      csv = "S.No,Date,Shift,Chilling Center,Plant,Start Time,Start FAT,Start SNF,Start Water%,Start Status,End Time,End FAT,End SNF,End Water%,End Status\n"
      results.forEach((r, i) => {
        csv += `${i + 1},"${r.date}","${r.shift}","${r.ccName}","${r.plantName}","${r.startTime}","${r.startFat}","${r.startSnf}","${r.startWater}","${r.startStatus}","${r.endTime}","${r.endFat}","${r.endSnf}","${r.endWater}","${r.endStatus}"\n`
      })
    }
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" })
    const link = document.createElement("a")
    link.href = URL.createObjectURL(blob)
    link.download = label + ".csv"
    link.click()
  }

  const cleaningStatusOptions = <>
    <option value="0">All</option>
    <option value="1">Not Done</option>
    <option value="2">Poor Cleaning (FAT or SNF is not 0)</option>
    <option value="3">Good Cleaning (FAT and SNF is 0)</option>
  </>

  // ── VLCC Tab Form ──
  const renderVlccForm = () => (
    <AvForm className="form-horizontal notToPrint" onValidSubmit={(e, v) => handleSubmit(e, v)}>
      {showCompany && <Row>
        <Col md={3}><div className="mb-3"><AvField type="select" name="company" label="Company" onChange={companyChange}><option value="">All</option>{companies.map((c, i) => <option key={i} value={c.id}>{c.name}</option>)}</AvField></div></Col>
      </Row>}
      <Row>
        <Col md={3}><div className="mb-3"><AvField type="select" name="plant" label="Plant" onChange={plantChange}><option value="">All</option>{plants.map((p, i) => <option key={i} value={p.id}>{p.name}</option>)}</AvField></div></Col>
        <Col md={3}><div className="mb-3"><AvField type="select" name="chillingCenter" label="Chilling Center" onChange={ccChange}><option value="">All Chilling Centers</option>{centers.map((c, i) => <option key={i} value={c.id}>{c.name}</option>)}</AvField></div></Col>
        <Col md={3}><div className="mb-3"><AvField type="select" name="route" label="Route" onChange={routeChange}><option value="">All Routes</option>{routes.map((r, i) => <option key={i} value={r.id}>{r.routeName}</option>)}</AvField></div></Col>
        <Col md={3}><div className="mb-3"><AvField type="select" name="center" label="Center"><option value="">All Centers</option>{collectionCenters.map((c, i) => <option key={i} value={c.id}>{c.centerName || c.name}</option>)}</AvField></div></Col>
      </Row>
      <Row>
        <Col md={3}><div className="mb-3"><AvField name="from" label="Period From *" type="date" value={today} required /></div></Col>
        <Col md={3}><div className="mb-3"><AvField name="to" label="Period To *" type="date" value={today} required /></div></Col>
        <Col md={3}><div className="mb-3"><AvRadioGroup inline name="shift" label="Shift" value="0" required><AvRadio label="Both" value="0" /><AvRadio label="AM" value="AM" /><AvRadio label="PM" value="PM" /></AvRadioGroup></div></Col>
        <Col md={3}><div className="mb-3"><AvField type="select" name="startCleaning" label="Start Cleaning Status">{cleaningStatusOptions}</AvField></div></Col>
      </Row>
      <Row>
        <Col md={3}><div className="mb-3"><AvField type="select" name="endCleaning" label="End Cleaning Status">{cleaningStatusOptions}</AvField></div></Col>
        <Col md={3} className="d-flex align-items-end pb-3">
          <button className="btn btn-primary w-md" type="submit" disabled={loading}>{loading ? <><Spinner size="sm" /> Submitting...</> : <><i className="fas fa-check me-1"></i>Submit</>}</button>
        </Col>
      </Row>
    </AvForm>
  )

  // ── CC Tab Form ──
  const renderCcForm = () => (
    <AvForm className="form-horizontal notToPrint" onValidSubmit={(e, v) => handleSubmit(e, v)}>
      {showCompany && <Row>
        <Col md={3}><div className="mb-3"><AvField type="select" name="company" label="Company" onChange={companyChange}><option value="">All</option>{companies.map((c, i) => <option key={i} value={c.id}>{c.name}</option>)}</AvField></div></Col>
      </Row>}
      <Row>
        <Col md={3}><div className="mb-3"><AvField type="select" name="plant" label="Plant" onChange={plantChange}><option value="">All</option>{plants.map((p, i) => <option key={i} value={p.id}>{p.name}</option>)}</AvField></div></Col>
        <Col md={3}><div className="mb-3"><AvField type="select" name="chillingCenter" label="Chilling Center"><option value="">All</option>{centers.map((c, i) => <option key={i} value={c.id}>{c.name}</option>)}</AvField></div></Col>
        <Col md={3}><div className="mb-3"><AvField name="from" label="Period From" type="date" value={today} required /></div></Col>
        <Col md={3}><div className="mb-3"><AvField name="to" label="Period To" type="date" value={today} required /></div></Col>
      </Row>
      <Row>
        <Col md={3}><div className="mb-3"><AvRadioGroup inline name="shift" label="Shift" value="0" required><AvRadio label="Both" value="0" /><AvRadio label="AM" value="AM" /><AvRadio label="PM" value="PM" /></AvRadioGroup></div></Col>
        <Col md={3}><div className="mb-3"><AvField type="select" name="startCleaning" label="Start Cleaning Status">{cleaningStatusOptions}</AvField></div></Col>
        <Col md={3}><div className="mb-3"><AvField type="select" name="endCleaning" label="End Cleaning Status">{cleaningStatusOptions}</AvField></div></Col>
        <Col md={3} className="d-flex align-items-end pb-3">
          <button className="btn btn-primary w-md" type="submit" disabled={loading}>{loading ? <><Spinner size="sm" /> Submitting...</> : <><i className="fas fa-check me-1"></i>Submit</>}</button>
        </Col>
      </Row>
    </AvForm>
  )

  const renderVlccTable = () => (
    <Table id="cleaningTbl" className="dairyTbl" size="sm" bordered>
      <thead>
        <tr>
          <th rowSpan="2">S.No</th><th rowSpan="2">Date</th><th rowSpan="2">Shift</th>
          <th rowSpan="2">Center</th><th rowSpan="2">Route</th><th rowSpan="2">CC</th>
          <th colSpan="5" className="text-center" style={{ background: "#e8f5e9" }}>Start Cleaning</th>
          <th colSpan="5" className="text-center" style={{ background: "#fce4ec" }}>End Cleaning</th>
        </tr>
        <tr>
          <th style={{ background: "#e8f5e9" }}>Time</th><th style={{ background: "#e8f5e9" }}>FAT</th>
          <th style={{ background: "#e8f5e9" }}>SNF</th><th style={{ background: "#e8f5e9" }}>Water%</th>
          <th style={{ background: "#e8f5e9" }}>Status</th>
          <th style={{ background: "#fce4ec" }}>Time</th><th style={{ background: "#fce4ec" }}>FAT</th>
          <th style={{ background: "#fce4ec" }}>SNF</th><th style={{ background: "#fce4ec" }}>Water%</th>
          <th style={{ background: "#fce4ec" }}>Status</th>
        </tr>
      </thead>
      <tbody>
        {results.length > 0 ? results.map((r, i) => (
          <tr key={i}>
            <td>{i + 1}</td><td>{r.date}</td><td>{r.shift}</td>
            <td>{r.centerName}</td><td>{r.routeName}</td><td>{r.ccName}</td>
            <td>{r.startTime}</td><td>{r.startFat}</td><td>{r.startSnf}</td><td>{r.startWater}</td><td>{getStatusBadge(r.startStatus)}</td>
            <td>{r.endTime}</td><td>{r.endFat}</td><td>{r.endSnf}</td><td>{r.endWater}</td><td>{getStatusBadge(r.endStatus)}</td>
          </tr>
        )) : <tr><td colSpan="16" className="text-center py-3">No Records Found</td></tr>}
      </tbody>
      {results.length > 0 && <tfoot><tr style={{ fontWeight: 700, background: "#f1f5f9" }}><td colSpan="16">Total Records: {results.length}</td></tr></tfoot>}
    </Table>
  )

  const renderCcTable = () => (
    <Table id="cleaningTbl" className="dairyTbl" size="sm" bordered>
      <thead>
        <tr>
          <th rowSpan="2">S.No</th><th rowSpan="2">Date</th><th rowSpan="2">Shift</th>
          <th rowSpan="2">Chilling Center</th><th rowSpan="2">Plant</th>
          <th colSpan="5" className="text-center" style={{ background: "#e8f5e9" }}>Start Cleaning</th>
          <th colSpan="5" className="text-center" style={{ background: "#fce4ec" }}>End Cleaning</th>
        </tr>
        <tr>
          <th style={{ background: "#e8f5e9" }}>Time</th><th style={{ background: "#e8f5e9" }}>FAT</th>
          <th style={{ background: "#e8f5e9" }}>SNF</th><th style={{ background: "#e8f5e9" }}>Water%</th>
          <th style={{ background: "#e8f5e9" }}>Status</th>
          <th style={{ background: "#fce4ec" }}>Time</th><th style={{ background: "#fce4ec" }}>FAT</th>
          <th style={{ background: "#fce4ec" }}>SNF</th><th style={{ background: "#fce4ec" }}>Water%</th>
          <th style={{ background: "#fce4ec" }}>Status</th>
        </tr>
      </thead>
      <tbody>
        {results.length > 0 ? results.map((r, i) => (
          <tr key={i}>
            <td>{i + 1}</td><td>{r.date}</td><td>{r.shift}</td>
            <td>{r.ccName}</td><td>{r.plantName}</td>
            <td>{r.startTime}</td><td>{r.startFat}</td><td>{r.startSnf}</td><td>{r.startWater}</td><td>{getStatusBadge(r.startStatus)}</td>
            <td>{r.endTime}</td><td>{r.endFat}</td><td>{r.endSnf}</td><td>{r.endWater}</td><td>{getStatusBadge(r.endStatus)}</td>
          </tr>
        )) : <tr><td colSpan="15" className="text-center py-3">No Records Found</td></tr>}
      </tbody>
      {results.length > 0 && <tfoot><tr style={{ fontWeight: 700, background: "#f1f5f9" }}><td colSpan="15">Total Records: {results.length}</td></tr></tfoot>}
    </Table>
  )

  const tabLabel = isVlcc ? "VLCC Cleaning Report" : "CC Cleaning Report"

  return (
    <div className="dashBody">
      <div className="font-size-18 mb-0 dashHeader notToPrint"><Row><Col md={12}><h4>{tabLabel}</h4></Col></Row></div>
      <div className="dashContent">
        <Nav tabs className="nav-tabs-custom mb-3 notToPrint">
          <NavItem><NavLink className={classnames({ active: activeTab === "1" })} onClick={() => setActiveTab("1")} style={{ cursor: "pointer" }}>VLCC Cleaning</NavLink></NavItem>
          <NavItem><NavLink className={classnames({ active: activeTab === "2" })} onClick={() => setActiveTab("2")} style={{ cursor: "pointer" }}>CC Cleaning</NavLink></NavItem>
        </Nav>
        <TabContent activeTab={activeTab}>
          <TabPane tabId="1">
            {activeTab === "1" && <>
              {renderVlccForm()}
              {submitted && <>
                {canExport(MID) && <div className="exportBtns notToPrint mb-2">
                  <button className="btn btn-info btn-sm m-1" onClick={downloadPdf}><i className="fas fa-file-pdf me-1"></i>PDF</button>
                  <button className="btn btn-info btn-sm m-1" onClick={() => window.print()}><i className="fas fa-print me-1"></i>Print</button>
                  <button className="btn btn-info btn-sm m-1" onClick={downloadCsv}><i className="fas fa-file-csv me-1"></i>CSV</button>
                </div>}
                <div className="table-responsive">{renderVlccTable()}</div>
              </>}
            </>}
          </TabPane>
          <TabPane tabId="2">
            {activeTab === "2" && <>
              {renderCcForm()}
              {submitted && <>
                {canExport(MID) && <div className="exportBtns notToPrint mb-2">
                  <button className="btn btn-info btn-sm m-1" onClick={downloadPdf}><i className="fas fa-file-pdf me-1"></i>PDF</button>
                  <button className="btn btn-info btn-sm m-1" onClick={() => window.print()}><i className="fas fa-print me-1"></i>Print</button>
                  <button className="btn btn-info btn-sm m-1" onClick={downloadCsv}><i className="fas fa-file-csv me-1"></i>CSV</button>
                </div>}
                <div className="table-responsive">{renderCcTable()}</div>
              </>}
            </>}
          </TabPane>
        </TabContent>
      </div>
    </div>
  )
}
export default CleaningReport
